/**
 * Admin certificate upload — single + bulk tabs.
 *
 * Phase: 6
 *
 * Tab 1 — Single: holder name + PDF file picker -> POST one certificate.
 * Tab 2 — Bulk:   ZIP picker (containing manifest.csv + PDFs) -> POST archive,
 *                  render the per-row result table from BulkUploadResponse.
 *
 * nginx caps body at 100MB (see nginx/nginx.conf::client_max_body_size).
 */

import {
  Alert,
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Card,
  FileInput,
  Group,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconCloudUpload, IconFile, IconFileZip } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  bulkUpload,
  getEvent,
  uploadCertificate,
} from "../../api/admin";
import { ApiError } from "../../api/client";
import type { BulkUploadResponse } from "../../api/types";

const MAX_BYTES = 100 * 1024 * 1024;

export function CertificateUpload() {
  const { eventId = "" } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const eventQuery = useQuery({
    queryKey: ["event", eventId],
    queryFn: () => getEvent(eventId),
    enabled: !!eventId,
  });

  // ─── Single upload ────────────────────────────────────────────────────────

  const singleForm = useForm<{ holder_name: string; file: File | null }>({
    initialValues: { holder_name: "", file: null },
    validate: {
      holder_name: (v) => (v.trim().length < 2 ? "İsim çok kısa." : null),
      file: (v) =>
        !v
          ? "PDF dosyası seçin."
          : v.type !== "application/pdf"
            ? "Sadece PDF dosyaları kabul edilir."
            : v.size > MAX_BYTES
              ? "Dosya 100MB sınırını aşıyor."
              : null,
    },
  });

  const [singleSuccess, setSingleSuccess] = useState<string | null>(null);
  const [singleError, setSingleError] = useState<string | null>(null);

  const singleMutation = useMutation({
    mutationFn: async (values: { holder_name: string; file: File | null }) => {
      if (!values.file) throw new Error("file required");
      return uploadCertificate(eventId, values.holder_name.trim(), values.file);
    },
    onSuccess: (cert) => {
      setSingleError(null);
      setSingleSuccess(`"${cert.holder_name}" yüklendi.`);
      singleForm.reset();
      queryClient.invalidateQueries({ queryKey: ["certificates", eventId] });
      queryClient.invalidateQueries({ queryKey: ["event", eventId] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
    onError: (err) => {
      setSingleSuccess(null);
      if (err instanceof ApiError) {
        setSingleError(
          (err.body as { detail?: string } | null)?.detail ?? "Yükleme başarısız oldu.",
        );
      } else {
        setSingleError("Yükleme başarısız oldu.");
      }
    },
  });

  // ─── Bulk upload ──────────────────────────────────────────────────────────

  const bulkForm = useForm<{ archive: File | null }>({
    initialValues: { archive: null },
    validate: {
      archive: (v) =>
        !v
          ? "ZIP dosyası seçin."
          : v.size > MAX_BYTES
            ? "Dosya 100MB sınırını aşıyor."
            : null,
    },
  });

  const [bulkResult, setBulkResult] = useState<BulkUploadResponse | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const bulkMutation = useMutation({
    mutationFn: async (values: { archive: File | null }) => {
      if (!values.archive) throw new Error("archive required");
      return bulkUpload(eventId, values.archive);
    },
    onSuccess: (result) => {
      setBulkError(null);
      setBulkResult(result);
      bulkForm.reset();
      queryClient.invalidateQueries({ queryKey: ["certificates", eventId] });
      queryClient.invalidateQueries({ queryKey: ["event", eventId] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
    onError: () => {
      setBulkResult(null);
      setBulkError("Toplu yükleme başarısız oldu. ZIP'i kontrol edin.");
    },
  });

  return (
    <Stack gap="md">
      <Breadcrumbs>
        <Anchor component={Link} to="/admin/events">
          Etkinlikler
        </Anchor>
        <Anchor component={Link} to={`/admin/events/${eventId}`}>
          {eventQuery.data?.name ?? "..."}
        </Anchor>
        <Text>Sertifika Yükle</Text>
      </Breadcrumbs>

      <Stack gap={2}>
        <Title order={2}>Sertifika Yükle</Title>
        <Text c="dimmed" size="sm">
          {eventQuery.data?.name} için tek tek veya toplu olarak sertifika ekleyin.
        </Text>
      </Stack>

      <Card withBorder shadow="xs">
        <Tabs defaultValue="single">
          <Tabs.List>
            <Tabs.Tab value="single" leftSection={<IconFile size={16} />}>
              Tek Sertifika
            </Tabs.Tab>
            <Tabs.Tab value="bulk" leftSection={<IconFileZip size={16} />}>
              Toplu (ZIP)
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="single" pt="md">
            <form
              onSubmit={singleForm.onSubmit((values) => singleMutation.mutate(values))}
            >
              <Stack gap="md">
                <TextInput
                  label="Sertifika Sahibi"
                  placeholder="Örn. Mehmet Akif"
                  required
                  {...singleForm.getInputProps("holder_name")}
                />
                <FileInput
                  label="PDF Dosyası"
                  description="Maksimum 100MB."
                  placeholder="PDF seçin"
                  accept="application/pdf"
                  leftSection={<IconCloudUpload size={16} />}
                  {...singleForm.getInputProps("file")}
                />

                {singleError && (
                  <Alert color="red" variant="light">
                    {singleError}
                  </Alert>
                )}
                {singleSuccess && (
                  <Alert color="green" variant="light">
                    {singleSuccess}
                  </Alert>
                )}

                <Group justify="end">
                  <Button
                    variant="default"
                    onClick={() => navigate(`/admin/events/${eventId}`)}
                  >
                    Kapat
                  </Button>
                  <Button type="submit" loading={singleMutation.isPending}>
                    Yükle
                  </Button>
                </Group>
              </Stack>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="bulk" pt="md">
            <form onSubmit={bulkForm.onSubmit((values) => bulkMutation.mutate(values))}>
              <Stack gap="md">
                <Alert color="blue" variant="light" title="ZIP biçimi">
                  <Text size="sm">
                    ZIP içinde <Text component="span" ff="monospace">manifest.csv</Text>{" "}
                    (sütunlar: <Text component="span" ff="monospace">holder_name, pdf_filename</Text>)
                    ve referans verilen PDF dosyaları bulunmalıdır.
                  </Text>
                </Alert>
                <FileInput
                  label="ZIP Arşivi"
                  description="Maksimum 100MB."
                  placeholder="ZIP seçin"
                  accept=".zip,application/zip"
                  leftSection={<IconFileZip size={16} />}
                  {...bulkForm.getInputProps("archive")}
                />

                {bulkError && (
                  <Alert color="red" variant="light">
                    {bulkError}
                  </Alert>
                )}

                <Group justify="end">
                  <Button
                    variant="default"
                    onClick={() => navigate(`/admin/events/${eventId}`)}
                  >
                    Kapat
                  </Button>
                  <Button type="submit" loading={bulkMutation.isPending}>
                    Yükle
                  </Button>
                </Group>
              </Stack>
            </form>

            {bulkResult && (
              <Stack gap="sm" mt="lg">
                <Group>
                  <Badge color="green" variant="light" size="lg">
                    {bulkResult.created} oluşturuldu
                  </Badge>
                  {bulkResult.errors.length > 0 && (
                    <Badge color="red" variant="light" size="lg">
                      {bulkResult.errors.length} hata
                    </Badge>
                  )}
                </Group>
                {bulkResult.errors.length > 0 && (
                  <Box style={{ overflowX: "auto" }}>
                    <Table withTableBorder verticalSpacing="xs">
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Satır</Table.Th>
                          <Table.Th>Sertifika Sahibi</Table.Th>
                          <Table.Th>PDF</Table.Th>
                          <Table.Th>Sebep</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {bulkResult.errors.map((row) => (
                          <Table.Tr key={row.row}>
                            <Table.Td>{row.row}</Table.Td>
                            <Table.Td>{row.holder_name ?? "—"}</Table.Td>
                            <Table.Td>
                              <Text size="sm" ff="monospace">
                                {row.pdf_filename ?? "—"}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm" c="red">
                                {row.reason}
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Box>
                )}
              </Stack>
            )}
          </Tabs.Panel>
        </Tabs>
      </Card>
    </Stack>
  );
}
